import React, { createContext, useContext, useState } from 'react';
import { message, Modal } from 'antd';
import { useEvent } from '../../../../../../hooks/useEvent';
import { roomStageIdsMap } from '../../../../../../constants/room/lifeCycle';
import ChangeStage from './ChangeStage';
import ChangeStep from './ChangeStep';
import { UNCHANGED_STATE } from './constants';
import { useAcceptanceViewFlatsInvalidate } from '../../../../api/acceptanceViewFlats';

const entities = {
  stage: 'stage',
  step: 'step',
};

export const MassChangeLifeCycle = { Provider, useActions };

const ActionsContext = createContext();

function useActions() {
  return useContext(ActionsContext);
}

function Provider({ onReset, children }) {
  const [roomsMap, setRoomsMap] = useState(() => new Map());

  const [modalData, setModalData] = useState();

  const invalidateRooms = useAcceptanceViewFlatsInvalidate();

  const selectChangeFn = (title, openModal) =>
    createSelectEntityChange({
      onReset: () => {
        onReset();
      },
      roomsMap,
      title,
      openModal,
    });

  const selectStageChange = useEvent(
    selectChangeFn(
      `Невозможно изменить стадию выбранных помещений`,
      (addressIds) => setModalData({ entity: entities.stage, addressIds })
    )
  );

  const selectStepChange = useEvent(
    selectChangeFn(
      `Невозможно изменить этап выбранных помещений`,
      (addressIds) => setModalData({ entity: entities.step, addressIds })
    )
  );

  const handleCancel = () => {
    setModalData();
  };

  const reset = () => {
    onReset();
    setModalData();
  };

  const createSettledHandler =
    ({ msg, title }) =>
    (data) => {
      if (data === UNCHANGED_STATE) {
        reset();
        return;
      }

      if (data === 'Error') {
        message.error(msg);
        reset();
        return;
      }

      const failedRooms = data.reduce((acc, { success, addressId }) => {
        if (!success) {
          const room = roomsMap.get(addressId);
          if (room) {
            acc.push(room);
          }
        }
        return acc;
      }, []);

      if (failedRooms.length === data.length) {
        message.error(msg);
        reset();
        return;
      }

      if (failedRooms.length > 0) {
        message.error(
          <FailedRoomsMessage failed={failedRooms} title={title} />
        );
      }

      reset();
      invalidateRooms();
    };

  const actions = useState(() => ({
    setRoomsMap,
    selectStageChange,
    selectStepChange,
  }))[0];

  return (
    <ActionsContext.Provider value={actions}>
      {children}
      <ChangeStage
        visible={modalData?.entity === entities.stage}
        addressIds={modalData?.addressIds}
        onCancel={handleCancel}
        onSettled={createSettledHandler({
          msg: 'Не удалось изменить стадию у выбранных помещений',
          title: 'Помещения, у которых не удалось изменить стадию',
        })}
      />
      <ChangeStep
        visible={modalData?.entity === entities.step}
        addressIds={modalData?.addressIds}
        onCancel={handleCancel}
        onSettled={createSettledHandler({
          msg: 'Не удалось изменить этап у выбранных помещений',
          title: 'Помещения, у которых не удалось изменить этап',
        })}
      />
    </ActionsContext.Provider>
  );
}

function createSelectEntityChange({ onReset, roomsMap, title, openModal }) {
  return (selectedIds) => {
    if (!Array.isArray(selectedIds) || selectedIds.length < 2) {
      return;
    }

    const stageIds = new Set();

    for (const id of selectedIds) {
      const stageId = roomsMap.get(id)?.stageAddressLifeCycle?.id;

      if (stageId) {
        stageIds.add(stageId);
      }

      if (stageIds.size > 1) {
        Modal.error({
          centered: true,
          title,
          content:
            'Все помещения должны находиться на одной стадии жизненного цикла',
          onOk: onReset,
        });
        return;
      }
    }

    if (stageIds.size === 1) {
      if (stageIds.has(roomStageIdsMap.acceptance)) {
        openModal(selectedIds);
      } else {
        Modal.error({
          centered: true,
          title,
          content:
            'Все помещения должны находиться на стадии «Приемка». В остальных случаях изменения доступны в модуле «Жилой фонд»',
          onOk: onReset,
        });
      }
    }
  };
}

function FailedRoomsMessage({ failed, title }) {
  return (
    <div>
      <div>{title}</div>
      <div style={{ marginTop: 8 }}>
        {failed.map((room) => (
          <div key={room.id}>
            {[room.typeName, room.flat].filter(Boolean).join(' ')}
          </div>
        ))}
      </div>
    </div>
  );
}
